import { filesApi } from '../data/filesApi';

/**
 * THE FILE-CONTENT TICKET, AND WHY IT MUST EXIST BEFORE THE APP PAINTS.
 *
 * Everything the browser fetches by itself — `<img src>`, `<audio src>`, a
 * download navigation — authorises through the httpOnly ticket, because none of
 * them can carry an Authorization header. That is not only chat attachments:
 * `avatarUrl` is `/v1/files/:id/content` too, so it is every avatar in the
 * product.
 *
 * Minting it in an effect loses a race it cannot win. Measured on the live site:
 *
 *     540ms  GET  401  /v1/files/…/content        <- an avatar
 *     610ms  POST 200  /v1/files/content-session  <- 70ms too late
 *
 * and the loser is not retried: `<img>` falls back to initials, `FileThumb`
 * latches its glyph, a media element latches its error state. So the mint is
 * awaited by the auth bootstrap BEFORE `status` becomes `authenticated`, which
 * means no authenticated view can paint before the cookie is in the jar.
 *
 * `ready` is exported for components that render file bytes outside that
 * guarantee (a ticket that lapses mid-session is re-minted on a timer).
 */

/** Never let a slow mint hold the sign-in transition. */
const MINT_TIMEOUT_MS = 2500;

let ready = false;
const listeners = new Set<() => void>();

export const isContentSessionReady = (): boolean => ready;

/** Server snapshot for `useSyncExternalStore` — never true during SSR. */
export const getContentSessionServerSnapshot = (): boolean => false;

export function setContentSessionReady(next: boolean): void {
  if (ready === next) return;
  ready = next;
  for (const listener of listeners) listener();
}

export function subscribeContentSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Mint the ticket. Resolves on success, on failure, and on the timeout — the
 * caller is gating a sign-in on this, so it must never be the thing that hangs.
 *
 * Marked ready even when the mint failed: the flag exists to stop a request that
 * is certain to 401, not to withhold images because one POST did not land.
 * Failing open leaves them exactly where they were before any of this existed.
 */
export async function ensureContentSession(): Promise<void> {
  const timeout = new Promise<void>((resolve) => {
    setTimeout(resolve, MINT_TIMEOUT_MS);
  });
  await Promise.race([filesApi.openContentSession().then(() => undefined, () => undefined), timeout]);
  setContentSessionReady(true);
}
